import { legalPageContent } from './legalContent';

import type { DocumentPageContent, LegalPageKey } from './legalContent';
import type { Locale } from '../i18n/types';

export type SitePageKey = 'home' | 'notFound' | LegalPageKey;

export interface LandingPageContent {
    kind: 'landing';
    eyebrow: string;
    title: string;
    description: string;
    primaryAction: string;
    privacyTitle: string;
    privacyDescription: string;
    modelsTitle: string;
    modelsDescription: string;
    flexibilityTitle: string;
    flexibilityDescription: string;
    seoTitle: string;
    seoDescription: string;
}

export type SitePageContent = LandingPageContent | DocumentPageContent;

export interface SiteRoute {
    key: SitePageKey;
    group: SitePageKey;
    locale: Locale;
    path: string;
    indexable: boolean;
    content: SitePageContent;
}

const pageContent: Readonly<Record<Locale, Readonly<Record<SitePageKey, SitePageContent>>>> = {
    en: {
        home: {
            kind: 'landing',
            eyebrow: 'PRIVATE · BROWSER-ONLY · OPEN SOURCE',
            title: 'Find a face in your media without uploading it anywhere.',
            description:
                'Face Lab brings every face-api.js model and its configuration into a focused reference-versus-search-area workflow that runs locally in your browser.',
            primaryAction: 'Open Face Lab',
            privacyTitle: 'Private by design',
            privacyDescription:
                'Files, remote media frames, face descriptors, and results stay in this browser session.',
            modelsTitle: 'Every supported model',
            modelsDescription:
                'Choose among four detectors, both landmark networks, recognition, expressions, age, and gender.',
            flexibilityTitle: 'Built for comparison',
            flexibilityDescription:
                'Use multiple reference faces and compare them with every face in the search area image or video frame.',
            seoTitle: 'Face Lab — Private browser face analysis',
            seoDescription:
                'Detect, analyze, and match faces locally in your browser with face-api.js.',
        },
        notFound: {
            kind: 'landing',
            eyebrow: '404 · PAGE NOT FOUND',
            title: 'This page is outside the search area.',
            description: 'Return to Face Lab or open the browser tool directly.',
            primaryAction: 'Back to home',
            privacyTitle: 'Private by design',
            privacyDescription: 'Your media stays on your device.',
            modelsTitle: 'Browser-based',
            modelsDescription: 'No upload service is involved.',
            flexibilityTitle: 'Ready when you are',
            flexibilityDescription: 'Open the tool to begin.',
            seoTitle: 'Page not found — Face Lab',
            seoDescription: 'The requested Face Lab page could not be found.',
        },
        ...legalPageContent.en,
    },
    tr: {
        home: {
            kind: 'landing',
            eyebrow: 'GİZLİ · TARAYICI İÇİ · AÇIK KAYNAK',
            title: 'Medyayı hiçbir yere yüklemeden içindeki yüzü bulun.',
            description:
                'Face Lab, face-api.js’in bütün modellerini ve ayarlarını tarayıcınızda çalışan odaklı bir referans–arama alanı akışında bir araya getirir.',
            primaryAction: 'Face Lab’i aç',
            privacyTitle: 'Tasarımı gereği özel',
            privacyDescription:
                'Dosyalar, uzak medya kareleri, yüz betimleri ve sonuçlar bu tarayıcı oturumunda kalır.',
            modelsTitle: 'Desteklenen tüm modeller',
            modelsDescription:
                'Dört dedektör, iki yüz işareti ağı, tanıma, ifade, yaş ve cinsiyet modellerini seçin.',
            flexibilityTitle: 'Karşılaştırma için üretildi',
            flexibilityDescription:
                'Birden fazla referans yüzünü arama alanı görselindeki veya video karesindeki tüm yüzlerle karşılaştırın.',
            seoTitle: 'Face Lab — Tarayıcıda özel yüz analizi',
            seoDescription:
                'face-api.js ile yüzleri tamamen tarayıcınızda tespit edin, analiz edin ve eşleştirin.',
        },
        notFound: {
            kind: 'landing',
            eyebrow: '404 · SAYFA BULUNAMADI',
            title: 'Bu sayfa arama alanının dışında kaldı.',
            description: 'Face Lab ana sayfasına dönün veya tarayıcı aracını doğrudan açın.',
            primaryAction: 'Ana sayfaya dön',
            privacyTitle: 'Tasarımı gereği özel',
            privacyDescription: 'Medyanız cihazınızda kalır.',
            modelsTitle: 'Tarayıcı tabanlı',
            modelsDescription: 'Herhangi bir yükleme servisi kullanılmaz.',
            flexibilityTitle: 'Hazır olduğunuzda',
            flexibilityDescription: 'Başlamak için aracı açın.',
            seoTitle: 'Sayfa bulunamadı — Face Lab',
            seoDescription: 'İstenen Face Lab sayfası bulunamadı.',
        },
        ...legalPageContent.tr,
    },
};

const routeBlueprints: readonly {
    key: SitePageKey;
    paths: Readonly<Record<Locale, string>>;
    indexable: boolean;
}[] = [
    { key: 'home', paths: { en: '/', tr: '/tr/' }, indexable: true },
    { key: 'privacy', paths: { en: '/privacy/', tr: '/tr/gizlilik/' }, indexable: true },
    { key: 'terms', paths: { en: '/terms/', tr: '/tr/kullanim-sartlari/' }, indexable: true },
    { key: 'notFound', paths: { en: '/404/', tr: '/tr/404/' }, indexable: false },
];

export const siteRoutes: readonly SiteRoute[] = routeBlueprints.flatMap((blueprint) =>
    (['en', 'tr'] as const).map((locale) => ({
        key: blueprint.key,
        group: blueprint.key,
        locale,
        path: blueprint.paths[locale],
        indexable: blueprint.indexable,
        content: pageContent[locale][blueprint.key],
    }))
);

function normalizePathname(pathname: string): string {
    const normalized = pathname.replace(/\/{2,}/g, '/');
    if (normalized === '/') return normalized;
    return normalized.endsWith('/') ? normalized : `${normalized}/`;
}

export function routeForKey(key: SitePageKey, locale: Locale): SiteRoute {
    const route = siteRoutes.find(
        (candidate) => candidate.key === key && candidate.locale === locale
    );
    if (route === undefined) throw new Error(`Site route is missing for ${key} (${locale})`);
    return route;
}

export function localizedRouteFor(route: SiteRoute, locale: Locale): SiteRoute {
    return routeForKey(route.group, locale);
}

export function resolveSiteRoute(pathname: string): SiteRoute {
    const normalizedPathname = normalizePathname(pathname);
    const route = siteRoutes.find((candidate) => candidate.path === normalizedPathname);
    if (route !== undefined) return route;
    return routeForKey('notFound', normalizedPathname.startsWith('/tr/') ? 'tr' : 'en');
}
